import axios from 'axios';
import { useEffect, useState } from 'react';
import isBase64 from 'validator/lib/isBase64';
import { Buffer } from 'buffer';
import { captureException } from '~/hooks/useExceptionLogger';
import { isAxiosError } from '~/utils/helpers';
import { downloadFileAsBuffer } from './download';

const loadXslDocument = async (xslUrl: string): Promise<XSLTProcessor> => {
  const xslDocument = await axios.get<XMLDocument>(xslUrl, {
    responseType: 'document',
    headers: {
      'Content-Type': 'application/xml; charset=utf-8',
    },
  });

  const xsltProcessor = new XSLTProcessor();
  xsltProcessor.importStylesheet(xslDocument.data);

  return xsltProcessor;
};

enum FileExtension {
  Xml = 'xml',
  P7m = 'p7m',
  Json = 'json',
  Other = 'other',
}

const fileExtension = (url: string): FileExtension => {
  try {
    const filePath = new URL(url).pathname.toLowerCase();
    if (filePath.endsWith('.xml')) {
      return FileExtension.Xml;
    } else if (filePath.endsWith('.p7m')) {
      return FileExtension.P7m;
    } else if (filePath.endsWith('.json')) {
      return FileExtension.Json;
    } else {
      return FileExtension.Other;
    }
  } catch (error) {
    captureException(error, {
      extra: {
        url,
        help_text: 'cannot determine the file extensions, fallback to other',
      },
    });
    return FileExtension.Other;
  }
};

export const isSupportedXmlDocumentUrl = (url: string) => {
  const extension = fileExtension(url);
  return extension === FileExtension.Xml || extension === FileExtension.P7m;
};

const parseXmlFile = async (url: string): Promise<Document | undefined> => {
  const document = await downloadFileAsBuffer(url, 'text/xml');
  return document ? parseXmlDocument(document, url) : undefined;
};

const decodeSignedXmlBase64File = (file: ArrayBuffer): ArrayBuffer => {
  const fileStr = Buffer.from(file).toString('ascii');

  // Since \n and \r are not allowed in base64, we remove them
  // before checking if the file is base64 encoded.
  if (!isBase64(fileStr.replace(/[\n\r]/g, ''))) return file;

  return Buffer.from(fileStr, 'base64').buffer;
};

const parseSignedXmlFile = async (url: string): Promise<Document | undefined> => {
  const file = await downloadFileAsBuffer(url, 'application/pkcs7-mime');
  if (!file) return;

  let xmlToParse: ArrayBuffer | undefined;
  try {
    const decodedFile = decodeSignedXmlBase64File(file);
    xmlToParse = await stripEnvelope(decodedFile);
  } catch (error) {
    captureException(error, {
      extra: {
        url,
        help_text:
          'something when wrong during strip envelope. The file could be a false signed file, we are considering it as a valid XML for next step.',
      },
    });

    xmlToParse = file;
  }
  return xmlToParse ? parseXmlDocument(xmlToParse, url) : undefined;
};

const parseXmlDocument = (data: ArrayBuffer, url: string): Document | undefined => {
  try {
    return new DOMParser().parseFromString(new TextDecoder().decode(data), 'text/xml');
  } catch (error) {
    captureException(error, {
      extra: {
        url,
        help_text: 'something when wrong during xml parsing',
      },
    });
    return;
  }
};

const stripEnvelope = async (data: ArrayBuffer): Promise<ArrayBuffer | undefined> => {
  const { ContentInfo, SignedData } = await import('pkijs');
  const cmsContentSimpl = ContentInfo.fromBER(data);
  const signedData = new SignedData({ schema: cmsContentSimpl.content });
  return signedData.encapContentInfo.eContent?.getValue();
};

const renderDocumentInsideNode = async (xmlDocument: Document, xslUrl: string): Promise<HTMLDivElement> => {
  const xsltProcessor = await loadXslDocument(xslUrl);
  const resultDocument = xsltProcessor.transformToFragment(xmlDocument, document);
  const rootElement = document.createElement('div');
  rootElement.appendChild(resultDocument);
  return rootElement;
};

export const loadXmlDocument = async (url: string): Promise<Document | undefined> => {
  switch (fileExtension(url)) {
    case FileExtension.Xml:
      return parseXmlFile(url);
    case FileExtension.P7m:
      return parseSignedXmlFile(url);
    default:
      return;
  }
};

type renderState =
  | { state: 'pending' }
  | { state: 'success'; html: HTMLDivElement }
  | { state: 'error'; error: 'EXPIRED_DOCUMENT' | 'MISSING_DOCUMENT' };

export const useRenderDocumentInsideNode = (xmlDocument: Document) => {
  const [state, setState] = useState<renderState>({ state: 'pending' });

  useEffect(() => {
    (async () => {
      try {
        const html = await renderDocumentInsideNode(xmlDocument, '/static/invoice.xsl');
        setState({ state: 'success', html });
      } catch (error) {
        captureException(error, {
          extra: {
            data: xmlDocument,
          },
        });
        const message = isAxiosError(error) && error.response?.status === 403 ? 'EXPIRED_DOCUMENT' : 'MISSING_DOCUMENT';

        setState({ state: 'error', error: message });
      }
    })();
  }, [xmlDocument]);

  return state;
};
